import DOMPurify from 'dompurify';
import {
  SANITIZE_CONFIG,
  DEFAULT_SANITIZE_CONFIG,
} from 'apps/website/themes/inline_editor/shared/dataHelper';

type ElementType = 'text' | 'textarea' | 'rich_text';

const INLINE_EDITING_ACTIVE_ATTRIBUTE = 'data-kjb-inline-editing-active';
const INLINE_EDITING_NOT_ALLOWED_ATTRIBUTE = 'data-kjb-inline-editing-not-allowed';
const EDITING_ATTRIBUTE = 'data-kjb-editing';
const ELEMENT_BACKGROUND_TYPE_ATTRIBUTE = 'data-kjb-background-type';
const DISABLE_EDITOR_OVERLAY_ATTRIBUTE = 'data-kjb-disable-editor-overlay';
export const ELEMENT_SETTINGS_ID_ATTRIBUTE = 'data-kjb-element-settings-id';

export const setEditorActiveAttributes = (element: HTMLElement) => {
  element.setAttribute(INLINE_EDITING_ACTIVE_ATTRIBUTE, 'true');

  // hide editor overlay
  const overlay = document.getElementById('editor-overlay');
  if (overlay) {
    overlay.style.display = 'none';
  }
};

export const removeEditorActiveAttributes = (element: HTMLElement) => {
  element.removeAttribute(INLINE_EDITING_ACTIVE_ATTRIBUTE);
};

export const isInBuilderEditingMode = () =>
  document.body.getAttribute(EDITING_ATTRIBUTE) === 'true';

export const canEditInline = (element: HTMLElement, elementType: ElementType) => {
  if (!isInBuilderEditingMode()) return false;

  const content = element.innerHTML.trim();

  // don't allow editing if content is empty
  if (!content.length) return false;

  // don't allow editing if content contains forbidden tags
  const sanitizedContent = DOMPurify.sanitize(content, sanitizeConfig(elementType)).trim();

  // tag attributes can be reordered so we compare content length
  // to make sure it's not changed after sanitization
  if (sanitizedContent.length !== content.length) {
    element.setAttribute(INLINE_EDITING_NOT_ALLOWED_ATTRIBUTE, 'true');
    element.removeAttribute(DISABLE_EDITOR_OVERLAY_ATTRIBUTE);
    return false;
  }

  return true;
};

const setElementStyleAttributes = (element: HTMLElement) => {
  if (element.hasAttribute(ELEMENT_BACKGROUND_TYPE_ATTRIBUTE)) {
    return;
  }

  const computedStyle = window.getComputedStyle(element);
  const textColor = computedStyle.color;
  const displayValue = computedStyle.display;
  const [r, g, b] = String(textColor).match(/\d+/g) || [];
  const isWhite = r === '255' && g === '255' && b === '255';
  const backgroundType = isWhite ? 'dark' : 'light';

  element.classList.add(`builder-text-${backgroundType}-background`);
  element.setAttribute(ELEMENT_BACKGROUND_TYPE_ATTRIBUTE, backgroundType);

  // force rectangular outline around inline elements
  if (displayValue === 'inline') {
    element.style.display = 'inline-block';
  }
};

export const initElementAttributes = (element: HTMLElement) => {
  element.classList.add('builder-text-editable');
  element.setAttribute('data-kjb-disable-editor-overlay', 'true');
  setElementStyleAttributes(element);
};

export const setContentForSiblingTextElements = (
  element: HTMLElement,
  content: string,
  elementType: ElementType,
) => {
  const settingsId = element.getAttribute(ELEMENT_SETTINGS_ID_ATTRIBUTE);
  if (!settingsId) return;

  const selector = `[${ELEMENT_SETTINGS_ID_ATTRIBUTE}="${settingsId}"]:not([${INLINE_EDITING_ACTIVE_ATTRIBUTE}])`;
  const siblings = document.querySelectorAll(selector);
  const sanitizedContent = DOMPurify.sanitize(content, sanitizeConfig(elementType));

  siblings.forEach((sibling) => {
    sibling.innerHTML = sanitizedContent;
  });
};

export const sanitizeConfig = (elementType: ElementType) =>
  SANITIZE_CONFIG[elementType] || DEFAULT_SANITIZE_CONFIG;
